// Wearing somebody else's colours (§26 C12: the IFF Spoofer).
//
// A disguised piece names an enemy piece and flies that army's transponder
// code. **It is a rule, where it used to be a belief**: the impersonated
// faction and its allies cannot capture the wearer (`canTake`) — which is why
// the state is `Position.disguise` and not the industry's.
//
// **Two ways off, where there were three.** It runs out (`expireDisguise`), or
// the wearer takes something of that faction's or an ally's. Gives-check went
// on §26.6 (6); out-of-character cost a `clonePosition` and a generation a move.
//
// **The AI's belief survives**: `seenAsEnemy` stops the spoofed army *marching
// at* a spy, which is a different claim from being unable to take it.

import { type Color, type PieceType } from "../board";
import { isCapture, type Move, moveCapturedSide } from "../move";
import {
  type Disguise,
  type Position,
  areEnemies,
  disguisedAs,
  setDisguise
} from "../position";
import { RULES } from "../rules";

/** Put a costume on, for `disguiseRounds` rounds (§31 T). */
export function wearDisguise(
  pos: Position,
  sq: number,
  side: Color,
  rank: PieceType
): void {
  setDisguise(pos, sq, {
    side,
    rank,
    until: pos.round + RULES.disguiseRounds
  });
}

/**
 * **Does `viewer` read the piece on `sq` as one of its own?**
 *
 * The one question `src/engine/ai/` asks, and the reason it is one function: a
 * disguise that fooled the pathfinder and not the capture scorer is an army
 * that walks past a piece and then takes it, which reads as the mechanic being
 * broken rather than as a rule.
 *
 * **Only the impersonated army is fooled.** A third army sees a stranger in
 * somebody else's uniform, which is still not one of *theirs*. The rule is
 * deliberately wider than the belief: that faction's allies may not take the
 * wearer either, and they are not fooled by it.
 */
export function readsAsAlly(pos: Position, viewer: Color, sq: number): boolean {
  const worn = disguisedAs(pos, sq);
  return worn !== null && worn.side === viewer;
}

/**
 * Is the piece on `sq` an enemy *as far as `viewer` can tell*?
 *
 * What every combat-facing question in `src/engine/ai/` should ask instead of
 * `areEnemies`. Industrial questions should not: a disguise is a uniform, not
 * a work permit, and an army that started routing ore into a spy's hands would
 * be handing the player its economy for a card.
 */
export function seenAsEnemy(
  pos: Position,
  viewer: Color,
  sq: number,
  side: Color
): boolean {
  if (!areEnemies(pos, viewer, side)) return false;
  return !readsAsAlly(pos, viewer, sq);
}

/** Why a disguise came off. Named, not a boolean: it is on the tick report. */
export type DisguiseBreak = "attacked";

/**
 * Has this move blown the cover?
 *
 * Asked in `applyMove` and never in `makeMove` — the queen's-sweep argument.
 *
 * **Whose the victim was, not whether there was one**: taking a third army's
 * piece is the case the designer's text carves out. `!areEnemies` covers the
 * faction and its allies in one reading.
 *
 * **A structure capture counts** — F5's framing is that a building is captured
 * like a piece, and `isCapture` is true for one. **A bash does not**: it never
 * carries `FLAG_CAPTURE` and the piece is still standing.
 */
export function disguiseBroken(
  pos: Position,
  move: Move,
  worn: Disguise
): DisguiseBreak | null {
  if (!isCapture(move)) return null;
  return areEnemies(pos, worn.side, moveCapturedSide(move)) ? null : "attacked";
}
